/**
 * Formats a number with locale-appropriate thousand separators.
 * Uses the browser's default locale (navigator.language).
 *
 * @example
 * formatNumber(5000)  // "5,000" (en-US), "5.000" (de-DE)
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(undefined).format(value);
}

/**
 * @deprecated Use `formatPrice` (which expects minimum currency units) instead.
 */
export function DEPRECATED_formatPriceInUsdDollars({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
}

/**
 * Format a price for display. Amount is in minimum currency units
 * (e.g. 12900 = $129.00 USD, 12900 = ¥12,900 JPY).
 *
 * Derives the divisor from the currency's default fraction digits
 * via Intl.NumberFormat (USD -> /100, JPY -> /1, etc.).
 *
 * @example
 * formatPrice({ amount: 12_900, currency: "usd" })  // "$129"
 * formatPrice({ amount: 7_220, currency: "usd" })   // "$72.20"
 * formatPrice({ amount: 12_900, currency: "jpy" })  // "¥12,900"
 */
export function formatPrice({
  amount,
  currency,
}: {
  amount: number;
  currency: string;
}): string {
  const majorUnits = amount / getMinorUnitDivisor(currency);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: Number.isInteger(majorUnits) ? 0 : 2,
  }).format(majorUnits);
}

function getMinorUnitDivisor(currency: string): number {
  const { minimumFractionDigits = 2 } = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).resolvedOptions();
  return 10 ** minimumFractionDigits;
}
